import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input } from '@angular/core';

// Services
import { EnvironmentService } from '../../../../../core/_services/environment/environment.service';

// Components
import { AlertComponent } from '../../../../../shared/_components/alert/alert.component';

// Models
import { StripeConfiguration } from '../../../../../core/_models/environment.model';
import { AlertType } from '../../../../../shared/_model/alert.model';
import { Theme } from '../../../../../core/_models/view-state.model';

// Directives
import { StateHandlerDirective } from '../../../../../core/_components/state-handler/state-handler.component';

// Matches the prefix the subscription webhooks resolve in user-services
// (`subscription-owner.ts`), and the team token convention in User-Integrations.
const TEAM_REFERENCE_PREFIX = 'TEAM-';

@Component({
  selector: 'pp-embedded-billing-web-view',
  templateUrl: './embedded-billing-web-view.component.html',
  styleUrl: './embedded-billing-web-view.component.scss',
  imports: [AlertComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EmbeddedBillingWebViewComponent extends StateHandlerDirective {

  readonly clientSecret = input<string>();
  readonly limitWidthOnPricingTable = input(false);

  // Aliases
  alertType = AlertType;

  // Access handlers
  clientEmail?: string;
  cognitoSub?: string;

  // Services
  private readonly environmentService = inject(EnvironmentService);

  readonly stripeEnvironmentSettings = computed<StripeConfiguration | undefined>(
    () => this.environmentService.environmentSettings()?.stripe,
  );

  constructor() {
    super();

    this.authService.getCurrentUser()
      .then((cognitoUser: any) => {
        this.cognitoSub = cognitoUser?.['sub'];
        this.clientEmail = cognitoUser?.['email'];
      });
  }

  /**
   * The reference Stripe carries through the checkout, identifying who the
   * subscription is for. A team subscription is bought through the same pricing
   * table and is marked with the `TEAM:` prefix, which the subscription
   * webhooks use to write the state onto the team rather than the buyer.
   * @type {string | undefined}
   * @memberof EmbeddedBillingWebViewComponent
   */
  /**
   * `true` unless this is a team context the current user may not buy for.
   *
   * The pricing table takes a payment method from whoever is looking at it, so
   * an ungated team checkout lets any member put the team's subscription on
   * their own personal card. Owners and admins only. The backend keys the
   * subscription off the reference below, so this is a UI guard on who is
   * offered the checkout, not an authorisation boundary.
   * @type {boolean}
   * @memberof EmbeddedBillingWebViewComponent
   */
  get canSubscribeTeam(): boolean {
    if (!this.viewState?.teamId) return true;
    const role = this.viewStateService.selectedTeamRole;
    return role === 'owner' || role === 'admin';
  }

  get clientReferenceId(): string | undefined {
    const teamId = this.viewState?.teamId;
    return teamId ? `${TEAM_REFERENCE_PREFIX}${teamId}` : this.cognitoSub;
  }

  /**
   * The Stripe pricing table identifier, by selected theme and account context.
   *
   * A team context is offered the business tables when they are configured
   * (#752). They are optional: while the business tables do not yet exist in
   * Stripe the team falls back to the personal table, which is wrong pricing but
   * a working page — better than an empty widget with no way back. Remove the
   * fallback once `teamPricingTableId*` is populated in every environment.
   * @type {string}
   * @memberof EmbeddedBillingWebViewComponent
   */
  get stripePricingTableId(): string {
    const isLight = this.selectedTheme === Theme.Light;
    const stripe = this.stripeEnvironmentSettings();

    if (this.viewState?.teamId) {
      const teamTable = isLight ?
        stripe?.teamPricingTableIdLight :
        stripe?.teamPricingTableIdDark;
      if (teamTable) return teamTable;
    }

    return (isLight ? stripe?.pricingTableIdLight : stripe?.pricingTableIdDark) ?? '';
  }

  get stripePublishableKey(): string {
    return this.stripeEnvironmentSettings()?.publishableKey ?? '';
  }
}
